import { Link } from 'react-router';
import { Apple, Check, Coffee, Plus, Soup, UtensilsCrossed, type LucideIcon } from 'lucide-react';
import type { TiempoComida } from '@/db/types';
import type { ResumenComidas, ResumenTiempo } from '@/domain/hoy';

/** Emblema de cada tiempo de comida, para distinguirlos de un vistazo (0.11.0). */
export const ICONO_TIEMPO: Record<TiempoComida, LucideIcon> = {
  desayuno: Coffee,
  comida: UtensilsCrossed,
  cena: Soup,
  colacion: Apple,
};

/**
 * Desayuno, comida y cena con palomita si hay registro y signo más si falta
 * (diseño Foco). Con `enlaceDe`, cada renglón lleva a su destino: registrar
 * esa comida si falta, o lo registrado si ya hay; sin él, solo informa.
 *
 * Cada tiempo lleva su emblema (0.11.0) para distinguirlos de un vistazo en
 * la tarjeta de Hoy.
 */
export function ListaComidas({
  comidas,
  enlaceDe,
}: {
  comidas: ResumenComidas;
  enlaceDe?: (tiempo: ResumenTiempo) => string;
}) {
  return (
    <ul className="divide-y divide-borde">
      {comidas.tiempos.map((t) => {
        const registrado = t.registros > 0;
        const Icono = ICONO_TIEMPO[t.tiempo];
        const contenido = (
          <>
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-superficie-suave text-marca">
              <Icono aria-hidden="true" className="size-[18px]" />
            </span>
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="text-[14px] font-medium text-texto">{t.etiqueta}</span>
              <span className="text-[12px] leading-[1.45] text-texto-suave">
                {t.descripcion ?? 'Sin registrar'}
              </span>
            </span>
            <span
              className={`grid size-8 shrink-0 place-items-center text-marca ${registrado ? '' : 'rounded-full bg-marca-suave'}`}
            >
              {registrado ? <Check className="size-[17px]" /> : <Plus className="size-[17px]" />}
              <span className="sr-only">
                {registrado ? 'Registrado: ver lo registrado' : 'Por registrar: agregar'}
              </span>
            </span>
          </>
        );
        const clases = 'flex min-h-[68px] w-full items-center gap-3 py-3 text-left';
        return (
          <li key={t.tiempo}>
            {enlaceDe === undefined ? (
              <div className={clases}>{contenido}</div>
            ) : (
              <Link to={enlaceDe(t)} className={`${clases} active:opacity-60`}>
                {contenido}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
